import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/shepherd-theme.css';

// Landing Page
import LandingPage from './pages/LandingPage';

// Auth Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';

// Main Pages
import Dashboard from './pages/Dashboard';
import Layout from './components/layout/Layout';
import Contact from './pages/Contact'; // Import the new Contact page
import PrivacyPolicy from './pages/PrivacyPage';
import OnboardingPage from './pages/OnboardingPage';
import TeamPage from './pages/TeamPage';
import TermsPage from './pages/TermsPage';

// Resume Pages
import ResumeUpload from './pages/resumes/ResumeUpload';
import ResumeList from './pages/resumes/ResumeList';
import ResumeEditor from './pages/resumes/ResumeEditor';
import ResumePolish from './pages/resumes/ResumePolish';
import ResumeMassage from './pages/resumes/ResumeMassage';
import CareerDirection from './pages/career/CareerDirection';

// Membership Pages
import MembershipComparison from './pages/membership/MembershipComparison';

// Job Pages
import JobList from './pages/jobs/JobList';
import JobCreation from './pages/jobs/JobCreation';
import JobEdit from './pages/jobs/JobEdit';
import JobApplication from './pages/jobs/JobApplication';
import ApplicationTracking from './pages/jobs/ApplicationTracking';
import JobMatchAnalysis from './pages/jobs/JobMatchAnalysis';
import MockInterview from './pages/jobs/MockInterview';
import MockInterviewSession from './pages/jobs/MockInterviewSession';

// ATS Scoring
import ATSScoring from './pages/jobs/ATSScoring';

// Diagnosis Pages
import ResumeDiagnosis from './pages/diagnosis/ResumeDiagnosis';
import DiagnosisReport from './pages/diagnosis/DiagnosisReport';

// Promo / public quick-diagnosis page
import QuickDiagnosis from './pages/promo/QuickDiagnosis';

// Admin Pages
import AdminRoute from './components/admin/AdminRoute';
import AdminDashboard from './pages/admin/Dashboard';
import UserManagement from './pages/admin/UserManagement';
import UserDetails from './pages/admin/UserDetails';
import MentorManagement from './pages/admin/MentorManagement';
import TicketManagement from './pages/admin/TicketManagement';
import TicketDetails from './pages/admin/TicketDetails';

// Mentor Pages
import MentorRoute from './components/mentor/MentorRoute';
import MentorDashboard from './pages/mentor/Dashboard';
import MenteeList from './pages/mentor/MenteeList';
import MenteeDetails from './pages/mentor/MenteeDetails';
import MentorTicketList from './pages/mentor/TicketList';
import JobAssignments from './pages/mentor/JobAssignments';

// PDF Generation

// Settings
import Settings from './pages/Settings';
import ProfessionalProfile from './pages/profile/ProfessionalProfile';


// Feedback Page
import FeedbackPage from './pages/FeedbackPage';

// Extension Auth
import ExtensionAuth from './pages/ExtensionAuth';

// Onboarding Components
import { OnboardingProvider } from './components/onboarding/OnboardingContext';
import { OnboardingTrigger } from './components/onboarding/OnboardingTrigger';

// Contexts
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { MembershipProvider } from './contexts/MembershipContext';
import { BalanceProvider } from './contexts/BalanceContext';
import { CacheProvider } from './contexts/CacheContext';
import { FeedbackProvider } from './contexts/FeedbackContext';

// Feedback Components
import { FeedbackSystem } from './components/feedback';

// Full-screen auth-check loader (teal brand spinner)
const PageLoader = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
    <div className="flex flex-col items-center gap-3">
      <div className="w-12 h-12 rounded-full border-4 border-primary-100 border-t-primary-500 animate-spin" />
      <p className="text-sm text-gray-400 tracking-wide">Loading…</p>
    </div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    // Store the current location they were trying to access
    const currentPath = location.pathname + location.search;
    localStorage.setItem('redirectAfterLogin', currentPath);
    return <Navigate to="/login" />;
  }

  return children;
};

// Home Route that redirects based on authentication status
const HomeRoute = () => {
  const { isAuthenticated, loading } = useAuth();

  // Show loading state while checking authentication
  if (loading) {
    return <PageLoader />;
  }
  
  // If authenticated, show Dashboard inside Layout
  if (isAuthenticated) {
    return (
      <ProtectedRoute>
          <Layout>
            <Dashboard />
          </Layout>
      </ProtectedRoute>
    );
  }
  
  // If not authenticated, show LandingPage
  return <LandingPage />;
};

function App() {
  return (
    <AuthProvider>
      <MembershipProvider>
        <BalanceProvider>
          <CacheProvider>
            <FeedbackProvider>
              <OnboardingProvider>
              <Router>
              <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
              <Routes>
            {/* Auth Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/extension-auth" element={<ExtensionAuth />} />
            <Route path="/onboarding" element={<OnboardingPage />} />

            {/* Public promo / lead-capture quick diagnosis */}
            <Route path="/free" element={<QuickDiagnosis />} />
            <Route path="/free/result/:leadId" element={<QuickDiagnosis />} />

          <Route path="/" element={<HomeRoute />} />
          
          {/* Contact Page Route */}
          <Route path="/contact" element={
            <ProtectedRoute>
              <Layout>
                <Contact />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Privacy Page Route */}
          <Route path="/privacy-policy" element={
              <Layout>
                <PrivacyPolicy />
              </Layout>
          } />

          {/* Team Page Route */}
          <Route path="/team" element={
              <Layout>
                <TeamPage />
              </Layout>
          } />

          {/* Terms Page Route */}
          <Route path="/terms" element={
              <Layout>
                <TermsPage />
              </Layout>
          } />
          
          {/* Resume Routes */}
          <Route path="/resumes" element={
            <ProtectedRoute>
              <Layout>
                <ResumeList />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/resumes/upload" element={
            <ProtectedRoute>
              <Layout>
                <ResumeUpload />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/resumes/:resumeId" element={
            <ProtectedRoute>
              <Layout>
                <ResumeEditor />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/resumes/:resumeId/polish" element={
            <ProtectedRoute>
              <Layout>
                <ResumePolish />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/resumes/:resumeId/massage" element={
            <ProtectedRoute>
              <Layout>
                <ResumeMassage />
              </Layout>
            </ProtectedRoute>
          } />
          
          {/* Job Routes */}
          <Route path="/jobs/saved" element={
            <ProtectedRoute>
              <Layout>
                <JobList />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/jobs/new" element={
            <ProtectedRoute>
              <Layout>
                <JobCreation />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/jobs/:jobId/edit" element={
            <ProtectedRoute>
              <Layout>
                <JobEdit />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/jobs/:jobId/apply" element={
            <ProtectedRoute>
              <Layout>
                <JobApplication />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/applications" element={
            <ProtectedRoute>
              <Layout>
                <ApplicationTracking />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/jobs/mock-interview" element={
            <ProtectedRoute>
              <Layout>
                <MockInterview />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/jobs/mock-interview/session/:applicationId" element={
            <ProtectedRoute>
              <Layout>
                <MockInterviewSession />
              </Layout>
            </ProtectedRoute>
          } />
  
        <Route path="/jobs/:jobId/match" element={
          <ProtectedRoute>
            <Layout>
              <JobMatchAnalysis />
            </Layout>
          </ProtectedRoute>
        } />
        
        {/* ATS Scoring Route */}
        <Route path="/ats-score" element={
          <ProtectedRoute>
            <Layout>
              <ATSScoring />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/resume-diagnosis" element={
          <ProtectedRoute>
            <Layout>
              <ResumeDiagnosis />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/resume-diagnosis/report" element={
          <ProtectedRoute>
            <Layout>
              <DiagnosisReport />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/jobs/:jobId/ats-score" element={
          <ProtectedRoute>
            <Layout>
              <ATSScoring />
            </Layout>
          </ProtectedRoute>
        } />
          
          
          {/* Settings */}
          <Route path="/settings" element={
            <ProtectedRoute>
              <Layout>
                <Settings />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Professional Profile */}
          <Route path="/profile" element={
            <ProtectedRoute>
              <Layout>
                <ProfessionalProfile />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Career Direction */}
          <Route path="/career-direction" element={
            <ProtectedRoute>
              <Layout>
                <CareerDirection />
              </Layout>
            </ProtectedRoute>
          } />
          
          {/* Membership */}
          <Route path="/membership" element={
            <ProtectedRoute>
              <Layout>
                <MembershipComparison />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Feedback Page Route */}
          <Route path="/feedback" element={
            <ProtectedRoute>
              <Layout>
                <FeedbackPage />
              </Layout>
            </ProtectedRoute>
          } />
 
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin/users" element={<AdminRoute><UserManagement /></AdminRoute>} />
          <Route path="/admin/users/:userId" element={<AdminRoute><UserDetails /></AdminRoute>} />
          <Route path="/admin/mentors/:mentorId/settings" element={<AdminRoute><MentorManagement /></AdminRoute>} />
          <Route path="/admin/tickets" element={<AdminRoute><TicketManagement /></AdminRoute>} />
          <Route path="/admin/tickets/:ticketId" element={<AdminRoute><TicketDetails /></AdminRoute>} />
          
          {/* Mentor Routes */}
          <Route path="/mentor/dashboard" element={
            <MentorRoute>
              <Layout>
                <MentorDashboard />
              </Layout>
            </MentorRoute>
          } />
          <Route path="/mentor/mentees" element={
            <MentorRoute>
              <Layout>
                <MenteeList />
              </Layout>
            </MentorRoute>
          } />
          <Route path="/mentor/mentees/:menteeId" element={
            <MentorRoute>
              <Layout>
                <MenteeDetails />
              </Layout>
            </MentorRoute>
          } />
          <Route path="/mentor/tickets" element={
            <MentorRoute>
              <Layout>
                <MentorTicketList />
              </Layout>
            </MentorRoute>
          } />
          <Route path="/mentor/job-assignments" element={
            <MentorRoute>
              <Layout>
                <JobAssignments />
              </Layout>
            </MentorRoute>
          } />
                    
          {/* Fallback route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
            
            {/* Global onboarding trigger that appears on all pages */}
            <OnboardingTrigger />
            
            {/* Global feedback system */}
            <FeedbackSystem />
          </Router>
          </OnboardingProvider>
            </FeedbackProvider>
          </CacheProvider>
        </BalanceProvider>
      </MembershipProvider>
    </AuthProvider>
  );
}

export default App;